import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Eye } from 'lucide-react';
import { products, waLink, waLinkProps } from '../mock';
import { parsePriceMXN } from '../lib/utils';
import { trackWhatsAppClick } from '../lib/analytics';
import WhatsAppIcon from './WhatsAppIcon';

const ProductCard = ({ product, large, onOpen, autoRotate, footerAction }) => {
  const mediaRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Modo Vitrina: la animación se reproduce al entrar en pantalla
  useEffect(() => {
    if (!autoRotate || !product.animation) return;
    const el = mediaRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [autoRotate, product.animation]);

  const pairProduct = product.pairId ? products.find((p) => p.id === product.pairId) : null;

  const offPercent = product.compareAtPrice
    ? Math.round((1 - parsePriceMXN(product.price) / parsePriceMXN(product.compareAtPrice)) * 100)
    : null;

  return (
  <article
    className={`q-card ${large ? 'q-card--large' : ''}`}
    data-testid={`product-card-${product.id}`}
  >
    <div
      ref={mediaRef}
      className="q-card__media"
      onClick={() => onOpen(product)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(product); }}
      data-testid={`product-media-${product.id}`}
    >
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="q-card__img"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
      {product.animation && (
        <img
          src={product.animation}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className={`q-card__img q-card__img--anim ${autoRotate && inView ? 'q-card__img--playing' : ''}`}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      )}
      {product.featured && (
        <span className="q-card__badge">
          <Sparkles size={12} strokeWidth={1.5} /> Pieza insignia
        </span>
      )}
      {product.stock === false && (
        <span className="q-card__badge q-card__badge--stock">Agotado</span>
      )}
      {offPercent > 0 && (
        <span className="q-card__badge q-card__badge--off">-{offPercent}% OFF</span>
      )}
      <span className="q-card__view">
        <Eye size={14} strokeWidth={1.5} /> Ver detalle
      </span>
    </div>
    <div className="q-card__body">
      <div className="q-card__meta">{product.material}</div>
      <h3
        className="q-card__name q-card__name--link"
        onClick={() => onOpen(product)}
        data-testid={`product-name-${product.id}`}
      >
        {product.name}
      </h3>
      <p className="q-card__desc">{product.description}</p>
      {pairProduct && (
        <p className="q-card__pair-hint" data-testid={`product-pair-hint-${product.id}`}>
          + Set con {pairProduct.name}
        </p>
      )}
      {product.variants && product.variants.length > 0 && (
        <p className="q-card__pair-hint" data-testid={`product-variants-hint-${product.id}`}>
          {product.variants.length} opciones disponibles
        </p>
      )}
      <div className="q-card__footer">
        <div className="q-card__price-group">
          {product.compareAtPrice && (
            <span className="q-card__price--compare">{product.compareAtPrice}</span>
          )}
          <span className="q-card__price" data-testid={`product-price-${product.id}`}>{product.price}</span>
        </div>
        {footerAction || (
          <a
            href={waLink(`Hola QUINLATE, me interesa cotizar: ${product.name}`)}
            {...waLinkProps}
            className="q-btn q-btn--ghost q-btn--sm q-btn--wa"
            data-testid={`product-cta-${product.id}`}
            aria-label={`Cotizar ${product.name} por WhatsApp`}
            onClick={() => trackWhatsAppClick('product_card', { product: product.id })}
          >
            <WhatsAppIcon size={17} />
          </a>
        )}
      </div>
    </div>
  </article>
  );
};

export default ProductCard;
